import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Button from '../components/common/Button';
import { getStats } from '../api';

const Home = () => {
  const [stats, setStats] = useState({
    total_students: 0,
    total_companies: 0,
    total_jobs: 0,
    total_applications: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getStats();
        setStats(response.data);
      } catch (err) {
        console.error('Failed to fetch stats', err);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="page-wrapper">
      <Navbar />
      
      <main className="main-content">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-grid-bg"></div>
          <div className="container relative z-10">
            <div className="hero-content">
              <div className="hero-eyebrow">
                🚀 The Future of Campus Placements
              </div>
              <h1 className="hero-title">
                Connect. <br/>
                <span className="highlight">Accelerate.</span><br/>
                Achieve.
              </h1>
              <p className="hero-subtitle">
                Million Minds is an AI-powered platform bridging the gap between ambitious students, top-tier colleges, and industry-leading companies.
              </p>
              
              <div className="hero-actions">
                <Link to="/register" className="btn btn-xl btn-accent">I'm a Student</Link>
                <Link to="/company/register" className="btn btn-xl btn-outline-white">I'm an Employer</Link>
                <Link to="/browse" className="btn btn-xl btn-ghost" style={{color: 'rgba(255,255,255,0.7)'}}>Browse Jobs</Link>
              </div>
              
              <div className="hero-stats">
                <div>
                  <div className="hero-stat-num">{stats.total_jobs || '500+'}</div>
                  <div className="hero-stat-label">Active Opportunities</div>
                </div>
                <div>
                  <div className="hero-stat-num">{stats.total_companies || '120+'}</div>
                  <div className="hero-stat-label">Hiring Partners</div>
                </div>
                <div>
                  <div className="hero-stat-num">{stats.total_students || '10k+'}</div>
                  <div className="hero-stat-label">Registered Students</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Top Recruiters Marquee */}
        <section className="section bg-white border-b border-gray-100 py-10">
          <div className="container">
            <p className="text-center text-sm font-semi text-gray-400 uppercase tracking-widest mb-6">Trusted by top companies</p>
            <div className="marquee-wrapper">
              <div className="marquee">
                {/* Simulated infinite scroll logos */}
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                  <div key={i} className="recruiter-chip">
                    Company {i}
                  </div>
                ))}
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                  <div key={`dup-${i}`} className="recruiter-chip">
                    Company {i}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="section bg-gray-50">
          <div className="container">
            <div className="section-header">
              <span className="section-eyebrow">Our Services</span>
              <h2 className="section-title">Everything you need to succeed</h2>
              <p className="section-desc">We provide end-to-end solutions for students, colleges, and companies to streamline the entire placement process.</p>
            </div>
            
            <div className="grid grid-3 gap-6">
              <div className="service-card">
                <div className="service-icon bg-primary-light text-primary">🎓</div>
                <h3 className="service-title">Internships & Placements</h3>
                <p className="service-desc">Access exclusive opportunities from top companies actively hiring from your college.</p>
              </div>
              <div className="service-card">
                <div className="service-icon bg-accent-light text-accent-dark">⚡</div>
                <h3 className="service-title">Skill Development</h3>
                <p className="service-desc">Curated courses and assessments to make you industry-ready before you graduate.</p>
              </div>
              <div className="service-card">
                <div className="service-icon bg-success-light text-success">📄</div>
                <h3 className="service-title">Resume Building</h3>
                <p className="service-desc">AI-powered resume builder that highlights your strengths and matches job descriptions.</p>
              </div>
              <div className="service-card">
                <div className="service-icon bg-warning-light text-warning">🎤</div>
                <h3 className="service-title">Mock Interviews</h3>
                <p className="service-desc">Practice with industry experts and AI to ace your technical and HR rounds.</p>
              </div>
              <div className="service-card">
                <div className="service-icon bg-primary-light text-primary">🤖</div>
                <h3 className="service-title">AI Career Counseling</h3>
                <p className="service-desc">Data-driven insights to help you choose the right career path based on your skills.</p>
              </div>
              <div className="service-card">
                <div className="service-icon bg-danger-light text-danger">🏢</div>
                <h3 className="service-title">College Dashboard</h3>
                <p className="service-desc">Comprehensive tools for placement officers to track, manage, and boost college placements.</p>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="section bg-white">
          <div className="container">
            <div className="grid grid-2 gap-16 items-center">
              <div>
                <span className="section-eyebrow">How It Works</span>
                <h2 className="section-title">Simple. Transparent.<br/>Effective.</h2>
                <p className="section-desc mb-8">Whether you're looking for your first job or looking to hire the best fresh talent, our process is streamlined for maximum efficiency.</p>
                
                <div className="flex-col gap-4">
                  <div className="step-card">
                    <div className="step-number">1</div>
                    <div>
                      <h4 className="step-content-title">Create your profile</h4>
                      <p className="step-content-desc">Sign up and build a comprehensive profile highlighting your skills and education.</p>
                    </div>
                  </div>
                  <div className="step-card">
                    <div className="step-number">2</div>
                    <div>
                      <h4 className="step-content-title">Discover matches</h4>
                      <p className="step-content-desc">Our AI matches you with the most relevant opportunities based on your profile.</p>
                    </div>
                  </div>
                  <div className="step-card">
                    <div className="step-number">3</div>
                    <div>
                      <h4 className="step-content-title">Apply & Get hired</h4>
                      <p className="step-content-desc">Apply with one click, track your application status, and ace your interviews.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-primary-light rounded-[32px] transform rotate-3 scale-105"></div>
                <div className="card-elevated relative z-10 p-8 rounded-[32px] bg-white text-center flex flex-col items-center justify-center min-h-[500px]">
                  <div className="text-6xl mb-6 animate-float">🚀</div>
                  <h3 className="heading-2 mb-4">Ready to launch your career?</h3>
                  <p className="text-gray-500 mb-8 max-w-sm mx-auto">Join thousands of students who have already found their dream jobs through Million Minds.</p>
                  <Link to="/register" className="btn btn-xl btn-primary w-full max-w-xs">Get Started Free</Link>
                  <Link to="/login" className="mt-4 text-sm font-semi text-primary hover:underline">Already have an account?</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default Home;
